import { apiFetch } from "./api";

// ==================== Types ====================

export type SlottingOffering = {
  offeringId: string;
  eventId: string;
  name: string;
  description?: string;
  bookingMode: "REQUEST_BASED" | "AUTO_BOOKABLE";
  timezone?: string;
  startDt?: string;
  endDt?: string;
  cancellationDeadlineHours?: number;
  maxBookingsPerGuest?: number;
  advanceBookingWindowHours?: number;
  overbookingPct?: number;
};

export type SlottingLane = {
  laneId: string;
  offeringId: string;
  name: string;
  treatmentIds?: string[];
  bufferMinutes?: number;
};

export type SlottingTreatment = {
  treatmentId: string;
  offeringId: string;
  name: string;
  description?: string;
  durationMinutes?: number;
  capacity?: number;
  imageUrl?: string;
};

export type AvailableSlot = {
  laneId: string;
  laneName: string;
  startDt: string;
  endDt: string;
  spotsLeft: number;
};

export type SlottingAppointment = {
  appointmentId: string;
  offeringId: string;
  treatmentId?: string;
  laneIds?: string[];
  guests?: { guestId: string; isCompanion: boolean }[];
  startDt: string;
  endDt: string;
  status: string;
  cancelledReason?: string;
};

export type SlottingWaitlistEntry = {
  waitlistEntryId: string;
  appointmentId: string;
  guestId: string;
  position?: number;
  status: string;
  createdAt?: string;
  promotedAt?: string;
};

export type LaneAvailabilityWindow = {
  laneId: string;
  laneName: string;
  startDt: string;
  endDt: string;
};

export type MyAppointmentSummary = {
  appointmentId: string;
  startDt: string;
  endDt: string;
  status: string;
  treatmentId?: string;
  treatmentName?: string;
};

export type MyRequestSummary = {
  requestId: string;
  treatmentNames?: string[];
  availability?: { startDt: string; endDt: string }[];
};

export type BookingPageData = {
  offering: SlottingOffering;
  lanes: SlottingLane[];
  treatments: SlottingTreatment[];
  availableSlots: AvailableSlot[];
  laneAvailabilities?: LaneAvailabilityWindow[];
  myAppointment?: MyAppointmentSummary;
  myRequest?: MyRequestSummary;
};

export type AppointmentPageData = {
  appointment: SlottingAppointment;
  offering: SlottingOffering | null;
  lane: SlottingLane | null;
  treatment: SlottingTreatment | null;
  rescheduled?: boolean;
  newAppointmentId?: string;
};

export type WaitlistPageData = {
  waitlistEntry: SlottingWaitlistEntry;
  appointment: SlottingAppointment | null;
  offering: SlottingOffering | null;
};

// ==================== API Functions ====================

export function fetchBookingPage(offeringId: string): Promise<BookingPageData> {
  return apiFetch<BookingPageData>(`/slotting/book/${offeringId}`);
}

export function fetchAppointmentPage(appointmentId: string): Promise<AppointmentPageData> {
  return apiFetch<AppointmentPageData>(`/slotting/appointment/${appointmentId}`);
}

export function fetchWaitlistPage(waitlistEntryId: string): Promise<WaitlistPageData> {
  return apiFetch<WaitlistPageData>(`/slotting/waitlist/${waitlistEntryId}`);
}

export function submitBooking(
  offeringId: string,
  body: {
    treatmentId?: string;
    laneId: string;
    startDt: string;
    endDt: string;
    companions?: string[];
  },
): Promise<{ appointment?: SlottingAppointment; appointmentId?: string; requestId?: string; message?: string }> {
  return apiFetch(`/slotting/book/${offeringId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function cancelAppointment(appointmentId: string): Promise<void> {
  return apiFetch(`/slotting/appointment/${appointmentId}`, { method: "DELETE" });
}

/** @deprecated Not currently used by runtime components */
export function rescheduleAppointment(
  appointmentId: string,
  body: { newLaneId: string; newStartDt: string; newEndDt: string },
): Promise<{ appointment?: SlottingAppointment; appointmentId?: string }> {
  return apiFetch(`/slotting/appointment/${appointmentId}/reschedule`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function submitRequest(
  offeringId: string,
  body: {
    treatmentIds: string[];
    availability: { startDt: string; endDt: string }[];
    companions?: string[];
  },
): Promise<{ requestId?: string; message?: string }> {
  return apiFetch(`/slotting/book/${offeringId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function cancelWaitlist(waitlistEntryId: string): Promise<void> {
  return apiFetch(`/slotting/waitlist/${waitlistEntryId}`, { method: "DELETE" });
}
